/**
 * Ce que l'écran d'un annuaire calcule, hors de React.
 *
 * Tout ce qui est ici est une fonction pure : mêmes entrées, mêmes sorties,
 * aucun appel réseau, aucun état. C'est ce qui rend l'écran vérifiable sans
 * navigateur — le reste (le rendu) se contrôle en regardant la page.
 *
 * **Paramétré par un descripteur**, comme `apps/api/src/annuaire.ts` l'est du
 * côté de l'API, et pour la même raison : l'écran d'un transporteur est celui
 * d'un client à quelques mots près. Deux fichiers jumeaux finiraient par
 * diverger — une correction portée d'un côté, oubliée de l'autre — et
 * l'utilisateur découvrirait la différence avant nous.
 *
 * **Ce que ce paramétrage coûte, et qui est assumé** : la saisie est
 * `Record<string, string>` et non plus un type dont les clés sont connues à la
 * compilation. Deux annuaires n'ont pas les mêmes champs ; les décrire par des
 * littéraux demanderait de rendre génériques la vue, le panneau et le
 * formulaire. Le contrôle reste fait, mais à l'exécution — par `validerFiche`
 * ici, par `refuserLesInconnus` dans l'API, et par la base ensuite.
 */

import type { Couverture, FicheDetail, LigneListe, Vehicule, Zone } from './api.js';

// ---------------------------------------------------------------------------
// Le descripteur d'un annuaire
// ---------------------------------------------------------------------------

/**
 * Un champ du formulaire.
 *
 * `court` : deux champs par ligne. Ils vont **par paires** — un `court` isolé
 * laisserait un trou à sa droite, constaté à l'écran le 03/09/2026.
 * `liste` : déroulant FERMÉ, on n'y saisit rien d'autre que ce qui est proposé.
 * `long` : zone de texte.
 */
export interface Champ {
  readonly cle: string;
  readonly etiquette: string;
  readonly max: number;
  readonly obligatoire?: boolean;
  readonly chiffre?: boolean;
  readonly court?: boolean;
  readonly long?: boolean;
  /**
   * `gabarits` s'ajoute le 03/09/2026 avec le parc : contrairement aux deux
   * autres, cette liste n'est pas écrite dans le code — elle vient du
   * référentiel commun des 24 gabarits, chargé à l'exécution. Le formulaire la
   * reçoit donc de son appelant plutôt que d'aller la chercher.
   */
  /**
   * `types_vehicule` s'ajoute le 04/09/2026 : le type d'un camion n'est plus du
   * texte libre mais une clé vers la liste réglée par la société (migration
   * 20260904170000). Comme les gabarits, cette liste vient de l'exécution.
   */
  /**
   * `frequences` s'ajoute le 09/09/2026 avec la facturation. Contrairement aux
   * pays et aux conditions, c'est une liste VRAIMENT fermée : la base refuse
   * toute autre valeur, parce que c'est elle qui décidera du regroupement des
   * factures.
   */
  readonly liste?: 'pays' | 'conditions' | 'gabarits' | 'types_vehicule' | 'frequences';
  /**
   * L'unité de ce qu'on saisit — « kg », « pal ». Elle est affichée à côté de
   * l'ÉTIQUETTE et jamais dans la case : « 24 000 kg » tapé dans un champ de
   * nombre serait refusé, et personne ne devrait avoir à deviner l'unité.
   */
  readonly unite?: string;
}

/** Un choix d'un déroulant : ce qui est enregistré, et ce qui se lit. */
export interface Choix {
  readonly valeur: string;
  readonly texte: string;
}

export interface Annuaire {
  /** Segment d'URL de l'API : « clients », « transporteurs ». */
  readonly chemin: string;
  /** Clé sous laquelle l'API rend la liste. */
  readonly cleListe: string;
  /** Clé sous laquelle l'API rend la fiche. */
  readonly cleFiche: string;
  /** « client ». Sert à écrire « Créer un client », « Nouveau client »… */
  readonly singulier: string;
  /** « clients ». Sert au décompte et à l'onglet de navigation. */
  readonly pluriel: string;
  readonly champs: readonly Champ[];
  /**
   * Cet annuaire porte-t-il une COUVERTURE — du matériel et des zones
   * desservies ? Vrai pour les transporteurs seuls. C'est ce qui fait
   * apparaître le cinquième onglet, et rien d'autre : ni le panneau ni
   * l'onglet lui-même ne savent de quel annuaire ils dessinent la fiche.
   */
  readonly couverture: boolean;
  /**
   * Cet annuaire porte-t-il des CONDITIONS de règlement ? Faux pour les sites :
   * un site est un lieu, pas une entité facturée. C'est ce qui fait disparaître
   * l'onglet Conditions de sa fiche — comme `couverture` fait apparaître le
   * cinquième onglet chez les seuls transporteurs.
   */
  readonly conditions: boolean;
  /**
   * Pied de l'onglet Conditions. **Le sens n'est pas le même des deux côtés** :
   * ce qu'un client nous doit, nous le devons à un transporteur. Absent quand
   * `conditions` est faux : il n'y aurait rien à en dire.
   */
  readonly noteConditions?: string;
  /** Complément du bandeau affiché sur une fiche désactivée. */
  readonly noteDesactive: string;
}

/** « clients » → « Clients ». L'onglet de navigation, sans le répéter. */
export function capitale(mot: string): string {
  return mot.charAt(0).toUpperCase() + mot.slice(1);
}

/**
 * Lit un champ de la fiche comme du texte affichable.
 *
 * Rend `null` plutôt qu'une chaîne vide : l'écran affiche « non renseigné » en
 * gris, et cette décision lui appartient.
 */
export function valeur(fiche: FicheDetail, cle: string): string | null {
  const v = fiche[cle];
  return typeof v === 'string' && v !== '' ? v : null;
}

/**
 * Cet annuaire porte-t-il ce champ ?
 *
 * Sépare deux choses que `valeur` confond, et que la fiche d'un site a rendues
 * visibles le 03/09/2026 : un champ VIDE, qui mérite « non renseigné » en gris,
 * et un champ qui N'EXISTE PAS dans cet annuaire, qui ne doit pas être écrit du
 * tout. Sans elle, la fiche d'un site annonçait un SIRET et un n° de TVA non
 * renseignés — alors qu'un site n'est pas une entité facturée et n'en aura
 * jamais.
 */
export function porte(annuaire: Annuaire, cle: string): boolean {
  return annuaire.champs.some((champ) => champ.cle === cle);
}

// ---------------------------------------------------------------------------
// Mise en forme et filtrage
// ---------------------------------------------------------------------------

/**
 * Retire les accents et met en minuscules, pour que « Zébulon » se trouve en
 * tapant « zebulon ».
 *
 * `normalize('NFD')` sépare la lettre de son accent ; l'expression régulière
 * retire ensuite les accents restés seuls. C'est la façon standard de comparer
 * deux textes français sans se soucier de la façon dont ils sont saisis.
 */
export function aplatir(texte: string): string {
  return texte
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

/**
 * Filtre la liste déjà chargée.
 *
 * Décidé le 03/09/2026 : la recherche se fait **dans le navigateur**, sur la
 * liste entière chargée une fois. Instantané, aucun appel à chaque touche. À
 * revoir si un annuaire dépasse le millier de lignes — celui des transporteurs
 * en comptera ≈ 260 après reprise, on le verra venir.
 *
 * On cherche dans le nom, le code et la localité : ce que la liste montre.
 * Chaque mot saisi doit se retrouver quelque part, dans n'importe quel ordre —
 * « ardec chartres » trouve donc la même fiche que « chartres ardec ».
 */
export function filtrer(
  lignes: readonly LigneListe[],
  recherche: string,
): readonly LigneListe[] {
  const mots = aplatir(recherche)
    .split(/\s+/)
    .filter((m) => m !== '');
  if (mots.length === 0) {
    return lignes;
  }

  return lignes.filter((ligne) => {
    const matiere = aplatir(
      [ligne.nom, ligne.code, ligne.code_postal, ligne.ville]
        .filter((v) => v !== null && v !== '')
        .join(' '),
    );
    return mots.every((mot) => matiere.includes(mot));
  });
}

/**
 * « 28000 Chartres », ou l'un des deux, ou rien.
 *
 * Rend `null` plutôt qu'une chaîne vide : l'écran affiche « non renseigné » en
 * gris, et cette décision lui appartient.
 */
export function localite(
  codePostal: string | null,
  ville: string | null,
): string | null {
  const morceaux = [codePostal, ville].filter(
    (m): m is string => m !== null && m.trim() !== '',
  );
  return morceaux.length === 0 ? null : morceaux.join(' ');
}

/**
 * « 12 000,00 € ». L'API rend un `numeric` sous forme de chaîne — jamais un
 * nombre flottant, qui perdrait des centimes.
 */
export function enEuros(montant: string | null): string | null {
  if (montant === null || montant.trim() === '') {
    return null;
  }
  const nombre = Number(montant);
  if (!Number.isFinite(nombre)) {
    return montant;
  }
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(nombre);
}

/** « 03/09/2026 ». Rend `null` si l'horodatage est illisible. */
export function dateCourte(horodatage: string | null): string | null {
  if (horodatage === null) {
    return null;
  }
  const date = new Date(horodatage);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(date);
}

/** « 3 clients — dont 1 inactif, affiché en gris », ou la variante au pluriel. */
export function decompte(lignes: readonly LigneListe[], annuaire: Annuaire): string {
  const total = lignes.length;
  const inactifs = lignes.filter((l) => !l.actif).length;
  const debut = `${total} ${total > 1 ? annuaire.pluriel : annuaire.singulier}`;

  if (inactifs === 0) {
    return debut;
  }
  return inactifs > 1
    ? `${debut} — dont ${inactifs} inactifs, affichés en gris`
    : `${debut} — dont 1 inactif, affiché en gris`;
}

// ---------------------------------------------------------------------------
// Le formulaire
// ---------------------------------------------------------------------------

/** Ce que le formulaire tient : une chaîne par champ, jamais `null`. */
export type Saisie = Readonly<Record<string, string>>;

export function saisieVide(annuaire: Annuaire): Saisie {
  return Object.fromEntries(annuaire.champs.map((c) => [c.cle, '']));
}

/** Remplit le formulaire à partir d'une fiche, pour la modification. */
export function saisieDepuis(
  fiche: { readonly [cle: string]: unknown },
  annuaire: Annuaire,
): Saisie {
  return Object.fromEntries(
    annuaire.champs.map((c) => {
      const v = fiche[c.cle];
      return [c.cle, typeof v === 'string' ? v : ''];
    }),
  );
}

const SIRET = /^[0-9]{14}$/;
const MONTANT = /^[0-9]{1,10}([.,][0-9]{1,2})?$/;

/**
 * Contrôle la saisie avant l'envoi, et rend un message par champ fautif.
 *
 * Ce contrôle **ne remplace pas celui de l'API** : il l'anticipe. L'API refuse
 * de toute façon, et la base derrière elle. L'intérêt est que la personne voie
 * l'erreur sous le champ concerné, tout de suite, plutôt qu'un bandeau qui ne
 * dit pas lequel.
 *
 * Les deux contrôles de forme — SIRET et montant — ne s'appliquent qu'aux
 * champs qui existent dans l'annuaire : demain, un annuaire sans SIRET ne
 * verrait rien tomber ici.
 */
export function validerFiche(
  saisie: Saisie,
  annuaire: Annuaire,
): Readonly<Record<string, string>> {
  const fautes: Record<string, string> = {};

  for (const champ of annuaire.champs) {
    const brut = saisie[champ.cle] ?? '';
    const valeur = brut.trim();

    if (valeur === '') {
      if (champ.obligatoire === true) {
        fautes[champ.cle] = 'Ce champ est obligatoire.';
      }
      continue;
    }
    if (valeur.length > champ.max) {
      fautes[champ.cle] = `${champ.max} caractères au plus.`;
    }
  }

  const siret = (saisie['siret'] ?? '').trim();
  if (siret !== '' && !SIRET.test(siret)) {
    fautes['siret'] = 'Quatorze chiffres, sans espace.';
  }

  const encours = (saisie['encours_autorise'] ?? '').trim();
  if (encours !== '' && !MONTANT.test(encours)) {
    fautes['encours_autorise'] = 'Un montant positif, deux décimales au plus.';
  }

  return fautes;
}

/**
 * Ce qui a changé entre la fiche chargée et la saisie — et rien d'autre.
 *
 * Un champ vidé devient `null` : l'API l'efface. Un champ non touché est
 * absent : l'API n'y touche pas. C'est ce qui rend le journal des traces
 * lisible — il ne garde que ce qui a réellement bougé — et ce qui évite
 * d'écraser le travail de quelqu'un d'autre sur un champ qu'on n'a pas ouvert.
 */
export function difference(
  avant: Saisie,
  apres: Saisie,
  annuaire: Annuaire,
): Readonly<Record<string, string | null>> {
  const change: Record<string, string | null> = {};

  for (const champ of annuaire.champs) {
    const initial = (avant[champ.cle] ?? '').trim();
    const saisi = (apres[champ.cle] ?? '').trim();
    if (initial === saisi) {
      continue;
    }
    change[champ.cle] = saisi === '' ? null : saisi;
  }

  return change;
}

/** Ce qu'on envoie à la création : les champs remplis, les autres omis. */
export function pourCreation(
  saisie: Saisie,
  annuaire: Annuaire,
): Readonly<Record<string, string>> {
  const corps: Record<string, string> = {};
  for (const champ of annuaire.champs) {
    const valeur = (saisie[champ.cle] ?? '').trim();
    if (valeur !== '') {
      corps[champ.cle] = valeur;
    }
  }
  return corps;
}

/**
 * « Le code DEL01 est déjà celui de Delta Pièces Auto. »
 *
 * L'unicité est tenue par l'index `(societe_id, code)` de la migration : c'est
 * la base qui refuse, et l'écran ne fait que le mettre en mots. Il peut nommer
 * l'autre fiche parce qu'il a déjà la liste entière — sans appel de plus.
 *
 * Le code peut appartenir à une fiche qui n'est pas dans la liste chargée
 * (créée par quelqu'un d'autre entre-temps) : on le dit alors sans la nommer,
 * plutôt que d'inventer.
 */
export function messageCodePris(
  lignes: readonly LigneListe[],
  code: string,
  annuaire: Annuaire,
  idCourant?: string,
): string {
  const cherche = code.trim();
  const autre = lignes.find(
    (l) => l.id !== idCourant && l.code !== null && l.code.trim() === cherche,
  );
  return autre === undefined
    ? `Le code ${cherche} est déjà pris par un autre ${annuaire.singulier}.`
    : `Le code ${cherche} est déjà celui de ${autre.nom}.`;
}

/**
 * Range la liste comme l'API : `order by lower(nom)`.
 *
 * L'ordre doit rester le même après une écriture qu'après un rechargement,
 * sans quoi une fiche renommée sauterait de place au prochain chargement.
 */
export function ranger(lignes: readonly LigneListe[]): readonly LigneListe[] {
  return [...lignes].sort((a, b) =>
    a.nom.toLowerCase().localeCompare(b.nom.toLowerCase(), 'fr'),
  );
}

/**
 * Pose une fiche créée ou modifiée dans la liste, et remet celle-ci en ordre.
 *
 * On repart de la fiche rendue par l'API, jamais de ce qui a été saisi : la
 * base a pu normaliser une valeur, et c'est elle qui fait foi.
 */
export function fusionner(
  lignes: readonly LigneListe[],
  fiche: LigneListe,
): readonly LigneListe[] {
  const ligne: LigneListe = {
    id: fiche.id,
    nom: fiche.nom,
    code: fiche.code,
    ville: fiche.ville,
    code_postal: fiche.code_postal,
    telephone: fiche.telephone,
    courriel: fiche.courriel,
    actif: fiche.actif,
  };
  const connue = lignes.some((l) => l.id === fiche.id);
  return ranger(
    connue ? lignes.map((l) => (l.id === fiche.id ? ligne : l)) : [...lignes, ligne],
  );
}

// ---------------------------------------------------------------------------
// Les listes fermées, et le code proposé
// ---------------------------------------------------------------------------

/**
 * Les conditions de paiement proposées. Liste fermée à l'écran, base ouverte —
 * même situation que les pays, et pour la même raison : `conditions_paiement`
 * est un `text` sans contrainte (vérifié dans la migration
 * 20260902153000_annuaire_clients.sql).
 *
 * Les trois premières sont celles que le transport routier permet. Les deux
 * dernières ont été demandées le 03/09/2026 malgré la remarque suivante, faite
 * avant de les écrire : **le délai de paiement du transport routier est plafonné
 * par la loi à trente jours**, plus court que le droit commun. Les proposer dans
 * une liste FERMÉE les rend faciles à choisir. La remarque est ici pour que
 * personne n'ait à la redécouvrir ; la décision appartient à l'utilisateur.
 */
/**
 * LES CINQ FRÉQUENCES DE FACTURATION — 09/09/2026, élargie le 10/09/2026.
 *
 * Mohamed en a demandé quatre le 10/09 : « A l'ordre , semaine , 15aine ,
 * mensuel ». `par_envoi` reste en plus des quatre, sur sa décision du même
 * jour : c'est le cas ordinaire, celui du client qui n'a rien négocié, et
 * c'est aussi la lecture de `null`.
 *
 * L'ORDRE N'EST PAS ALPHABÉTIQUE, il est chronologique : du plus fréquent au
 * plus espacé. Un déroulant se lit comme une échelle, pas comme un
 * dictionnaire.
 *
 * La seule liste du fichier dont la valeur enregistrée DIFFÈRE de ce qui se
 * lit, et c'est ce qui la distingue des pays et des conditions de paiement :
 * `par_envoi` est une clé que le code lira pour regrouper, « Une facture par
 * envoi » est une phrase que l'humain lit. Renommer la phrase un jour ne
 * cassera rien.
 *
 * `null` en base — le déroulant offre « — » — veut dire « pas réglé », et se
 * lit comme `par_envoi`. On n'affirme pas un accord commercial que personne
 * n'a pris.
 */
export const FREQUENCES_FACTURATION: readonly Choix[] = [
  { valeur: 'par_envoi', texte: 'Une facture par envoi' },
  { valeur: 'par_ordre', texte: 'Une facture par ordre' },
  { valeur: 'par_semaine', texte: 'Une facture par semaine' },
  { valeur: 'par_quinzaine', texte: 'Une facture par quinzaine' },
  { valeur: 'par_mois', texte: 'Une facture par mois' },
];

/**
 * La fréquence, écrite en français. « — » quand elle n'est pas réglée.
 *
 * Pourquoi une fonction et non un simple `find` à l'écran : la fiche montre la
 * VALEUR ENREGISTRÉE, et `par_mois` ne se lit pas. Une fiche ancienne peut
 * aussi porter une valeur devenue inconnue — la base la refuse aujourd'hui,
 * mais une clé retirée un jour de la liste resterait en base. On rend alors la
 * clé brute plutôt que rien : même règle que `zoneDite` de l'affrètement.
 */
export function frequenceDite(valeur: string | null): string | null {
  if (valeur === null || valeur === '') return null;
  return FREQUENCES_FACTURATION.find((f) => f.valeur === valeur)?.texte ?? valeur;
}

export const CONDITIONS_PAIEMENT: readonly string[] = [
  'Comptant à réception',
  '30 jours date de facture',
  '30 jours fin de mois',
  '45 jours fin de mois',
  '60 jours date de facture',
];

/**
 * Les valeurs à offrir dans un déroulant, la valeur actuelle comprise.
 *
 * **Le point qui compte** : une fiche ancienne peut porter une valeur absente
 * de la liste — « FRANCE » en capitales, saisi avant que la liste existe. Sans
 * ce rattrapage, ouvrir le formulaire afficherait autre chose que ce qui est
 * enregistré, et un simple aller-retour changerait le pays sans que personne
 * l'ait demandé. On l'ajoute donc en tête, et la fiche garde sa valeur tant
 * qu'on n'y touche pas.
 */
export function options(
  liste: readonly string[],
  valeurActuelle: string,
): readonly string[] {
  const valeur = valeurActuelle.trim();
  if (valeur === '' || liste.includes(valeur)) {
    return liste;
  }
  return [valeur, ...liste];
}

/**
 * Le même rattrapage, quand ce qui est enregistré n'est pas ce qui se lit.
 *
 * Le gabarit d'une remorque est enregistré « T6 » et se lit « Semi tautliner ».
 * Une valeur absente du référentiel — un code retiré depuis — est remise en
 * tête sous sa forme brute : mieux vaut lire « T6 » que de voir le champ se
 * vider tout seul.
 */
export function choix(
  liste: readonly Choix[],
  valeurActuelle: string,
): readonly Choix[] {
  const valeur = valeurActuelle.trim();
  if (valeur === '' || liste.some((c) => c.valeur === valeur)) {
    return liste;
  }
  return [{ valeur, texte: valeur }, ...liste];
}

/**
 * Le code proposé pour une nouvelle fiche : quatre lettres du nom, puis un
 * numéro à deux chiffres.
 *
 * « TNS Logistique » donne TNSL01 — exactement ce que l'utilisateur avait tapé
 * lui-même avant que cette fonction existe. Accents retirés, majuscules, tout
 * ce qui n'est pas une lettre ignoré. Si le code est déjà porté par une fiche
 * de la société, on essaie 02, 03, et ainsi de suite.
 *
 * Ce n'est qu'une PROPOSITION : elle remplit un champ vide, et se corrige à la
 * main. L'unicité, elle, reste tenue par l'index `(societe_id, code)` de la
 * base — l'écran ne fait que réduire les collisions, il ne les interdit pas.
 * Une fiche créée par quelqu'un d'autre pendant la saisie ne serait pas dans
 * la liste chargée, et la base refuserait alors le code.
 */
export function codePropose(nom: string, lignes: readonly LigneListe[]): string {
  const lettres = aplatir(nom)
    .replace(/[^a-z]/g, '')
    .toUpperCase();

  if (lettres === '') {
    return '';
  }

  const racine = lettres.slice(0, 4);
  const pris = new Set(
    lignes
      .map((l) => l.code)
      .filter((c): c is string => c !== null)
      .map((c) => c.trim().toUpperCase()),
  );

  for (let numero = 1; numero <= 99; numero += 1) {
    const essai = `${racine}${String(numero).padStart(2, '0')}`;
    if (!pris.has(essai)) {
      return essai;
    }
  }

  // Quatre-vingt-dix-neuf fiches dont le nom commence pareil : on rend la
  // racine seule et on laisse la personne trancher. Mieux qu'un code inventé
  // que la base refuserait.
  return racine;
}

// ---------------------------------------------------------------------------
// Les contacts — les mêmes sous un client et sous un transporteur
// ---------------------------------------------------------------------------

/**
 * Les champs d'un contact. Miroir de `CHAMPS_CONTACT` dans
 * `apps/api/src/contacts.ts`, longueurs maximales comprises.
 *
 * Ils ne dépendent pas de l'annuaire : un contact de transporteur porte les
 * mêmes colonnes qu'un contact de client, décidé le 03/09/2026. Une différence
 * entre les deux écrans sans raison serait une différence à expliquer.
 *
 * Deux numéros nommés, pas un champ où l'on taperait les deux : « Téléphone »
 * et « Portable ». Demandé le 03/09/2026, après le premier usage réel. En
 * exploitation, on veut savoir lequel joint la personne sur la route.
 */
export const CHAMPS_CONTACT = [
  { cle: 'nom', etiquette: 'Nom', max: 200, obligatoire: true },
  { cle: 'fonction', etiquette: 'Fonction', max: 120 },
  { cle: 'telephone', etiquette: 'Téléphone', max: 40, chiffre: true, court: true },
  { cle: 'mobile', etiquette: 'Portable', max: 40, chiffre: true, court: true },
  { cle: 'courriel', etiquette: 'Courriel', max: 200 },
  { cle: 'note', etiquette: 'Note', max: 2000, long: true },
] as const;

export type CleChampContact = (typeof CHAMPS_CONTACT)[number]['cle'];

/**
 * `principal` est à part des autres champs, et volontairement : c'est une case
 * à cocher, pas un champ de saisie. Le mêler aux chaînes obligerait à
 * l'encoder en « oui » / « » et à le décoder partout.
 */
export type SaisieContact = Readonly<Record<CleChampContact, string>> & {
  readonly principal: boolean;
};

export const CONTACT_VIDE: SaisieContact = {
  ...(Object.fromEntries(CHAMPS_CONTACT.map((c) => [c.cle, ''])) as Record<
    CleChampContact,
    string
  >),
  principal: false,
};

export function contactDepuis(contact: {
  readonly [cle: string]: unknown;
}): SaisieContact {
  return {
    ...(Object.fromEntries(
      CHAMPS_CONTACT.map((c) => {
        const valeur = contact[c.cle];
        return [c.cle, typeof valeur === 'string' ? valeur : ''];
      }),
    ) as Record<CleChampContact, string>),
    principal: contact['principal'] === true,
  };
}

export function validerContact(
  saisie: SaisieContact,
): Readonly<Partial<Record<CleChampContact, string>>> {
  const fautes: Partial<Record<CleChampContact, string>> = {};

  for (const champ of CHAMPS_CONTACT) {
    const valeur = saisie[champ.cle].trim();
    if (valeur === '') {
      if ('obligatoire' in champ && champ.obligatoire) {
        fautes[champ.cle] = 'Ce champ est obligatoire.';
      }
      continue;
    }
    if (valeur.length > champ.max) {
      fautes[champ.cle] = `${champ.max} caractères au plus.`;
    }
  }

  return fautes;
}

/** Même règle que pour une fiche : on n'envoie que ce qui a changé. */
export function differenceContact(
  avant: SaisieContact,
  apres: SaisieContact,
): Readonly<Partial<Record<CleChampContact, string | null> & { principal: boolean }>> {
  const change: Partial<
    Record<CleChampContact, string | null> & { principal: boolean }
  > = {};
  for (const champ of CHAMPS_CONTACT) {
    const initial = avant[champ.cle].trim();
    const saisi = apres[champ.cle].trim();
    if (initial !== saisi) {
      change[champ.cle] = saisi === '' ? null : saisi;
    }
  }
  if (avant.principal !== apres.principal) {
    change.principal = apres.principal;
  }
  return change;
}

export function pourCreationContact(
  saisie: SaisieContact,
): Readonly<Partial<Record<CleChampContact, string> & { principal: boolean }>> {
  const corps: Partial<Record<CleChampContact, string> & { principal: boolean }> = {};
  for (const champ of CHAMPS_CONTACT) {
    const valeur = saisie[champ.cle].trim();
    if (valeur !== '') {
      corps[champ.cle] = valeur;
    }
  }
  // Coché seulement : la valeur par défaut de la colonne est déjà « faux », il
  // n'y a rien à envoyer pour un contact ordinaire.
  if (saisie.principal) {
    corps.principal = true;
  }
  return corps;
}

/**
 * Ce qui empêche — ou non — de supprimer une fiche désactivée.
 *
 * Aujourd'hui seuls les contacts la retiennent : les tables des transports et
 * des factures n'existent pas encore. Quand elles existeront, leurs décomptes
 * viendront s'ajouter ici, et la phrase les nommera de la même façon.
 *
 * **La règle n'est pas tenue par cette phrase** : c'est `on delete restrict` en
 * base qui refuse, et l'API qui traduit. L'écran ne fait qu'annoncer d'avance
 * ce que la base répondrait.
 */
export function messageRattachement(
  nombreContacts: number,
  annuaire: Annuaire,
): string {
  if (nombreContacts === 0) {
    return `Rien n’est rattaché à ce ${annuaire.singulier} : la suppression est possible.`;
  }
  return nombreContacts === 1
    ? 'Suppression impossible : 1 contact y est rattaché.'
    : `Suppression impossible : ${nombreContacts} contacts y sont rattachés.`;
}

// ---------------------------------------------------------------------------
// La couverture d'un transporteur — le calcul de l'onglet, hors de React
// ---------------------------------------------------------------------------

/**
 * Groupe le matériel par carrosserie, dans l'ordre où le référentiel le rend.
 *
 * Constaté à l'écran le 03/09/2026 : posées dans une grille de trois colonnes,
 * les 24 cases se remplissaient LIGNE PAR LIGNE, et le regroupement par
 * carrosserie — qui est tout le sens d'une grille carrosserie × gamme —
 * disparaissait. « Tous les frigos » demandait de balayer les 24.
 */
export function parCarrosserie(
  vehicules: readonly Vehicule[],
): readonly { readonly nom: string; readonly lignes: readonly Vehicule[] }[] {
  const groupes: { nom: string; lignes: Vehicule[] }[] = [];
  for (const v of vehicules) {
    const dernier = groupes[groupes.length - 1];
    if (dernier !== undefined && dernier.nom === v.carrosserie_nom) {
      dernier.lignes.push(v);
    } else {
      groupes.push({ nom: v.carrosserie_nom, lignes: [v] });
    }
  }
  return groupes;
}

/**
 * Les départements desservis d'abord, les autres ensuite.
 *
 * Constaté à l'écran : sur 101 lignes, les trois qui sont cochées ne se voient
 * pas — il faut descendre jusqu'au 28 pour apprendre ce que ce transporteur
 * dessert, alors que c'est la question qu'on se pose en ouvrant l'onglet.
 *
 * L'ordre se règle sur ce qui est ENREGISTRÉ, et non sur ce qui est coché :
 * sinon une case cochée sauterait en tête sous les doigts, comme le code
 * proposé qui « danserait » s'il se recalculait à chaque lettre.
 */
export function desservisDAbord(
  referentiel: readonly Zone[],
  enregistrees: readonly Couverture[],
): readonly Zone[] {
  const desservis = new Set(enregistrees.map((z) => z.zone));
  return [
    ...referentiel.filter((z) => desservis.has(z.code)),
    ...referentiel.filter((z) => !desservis.has(z.code)),
  ];
}

/** Deux listes de codes portent-elles le même contenu, l'ordre en moins ? */
export function memesCodes(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const gauche = [...a].sort();
  const droite = [...b].sort();
  return gauche.every((code, i) => code === droite[i]);
}

/** Même question pour les zones, les deux sens compris. */
export function memesZones(
  a: readonly Couverture[],
  b: readonly Couverture[],
): boolean {
  if (a.length !== b.length) return false;
  const parCode = new Map(b.map((z) => [z.zone, z]));
  return a.every((z) => {
    const autre = parCode.get(z.zone);
    return (
      autre !== undefined && autre.depart === z.depart && autre.arrivee === z.arrivee
    );
  });
}

/** Coche ou décoche un matériel. L'ordre d'arrivée n'a pas d'importance : la
 * fiche rendue par l'API remet la liste dans l'ordre du référentiel. */
export function basculerCode(
  codes: readonly string[],
  code: string,
  coche: boolean,
): readonly string[] {
  return coche ? [...codes, code] : codes.filter((c) => c !== code);
}

/**
 * Coche ou décoche un sens sur un département.
 *
 * **Décocher les DEUX retire la ligne.** Une zone qui ne dit ni départ ni
 * arrivée ne dit rien : la base la refuse par `check (depart or arrivee)`, et
 * l'écran ne doit pas la construire. C'est la règle qui justifie que cette
 * fonction existe plutôt qu'un `setState` écrit dans l'écran.
 */
export function basculerZone(
  zones: readonly Couverture[],
  zone: string,
  sens: 'depart' | 'arrivee',
  coche: boolean,
): readonly Couverture[] {
  const ancienne = zones.find((z) => z.zone === zone);
  const depart = sens === 'depart' ? coche : (ancienne?.depart ?? false);
  const arrivee = sens === 'arrivee' ? coche : (ancienne?.arrivee ?? false);
  const autres = zones.filter((z) => z.zone !== zone);

  return depart || arrivee ? [...autres, { zone, depart, arrivee }] : autres;
}
